import { useState } from 'react';

import { Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { ptBR } from 'date-fns/locale';

import { GradeAutoComplete, ProdutoAutoComplete, SituacaoConfiguracaoPedidoAutoComplete, TipoPedidoAutoComplete } from 'components/autocompletes';
import { GradeOptionsProps } from 'components/autocompletes/GradeAutoComplete';
import { ProdutoProps } from 'components/autocompletes/ProdutoAutoComplete';
import { CissTextField, StatusField } from 'components/fields';
import { Filter } from 'components/filter';
import { FilterValuesProps } from 'hooks/useFilter';

const getInicioVigencia = () => new Date(new Date().getFullYear(), 0, 1);
const getFimVigencia = () => new Date(2099, 11, 31);

export function ConfigPedidoGridFilter() {
    const [filterProduto, setFilterProduto] = useState<FilterValuesProps>({
        property: 'idProduto',
        operator: 'in',
        value: [],
        idProperty: 'idProduto',
    });

    const [filterGrade, setFilterGrade] = useState<FilterValuesProps>({
        property: 'idTipoGrade',
        operator: 'in',
        value: [],
        idProperty: 'idTipoGrade',
    });

    const [filterSitucao, setFilterSitucao] = useState<FilterValuesProps>({
        property: 'idConfigPedidoStatus',
        operator: 'in',
        value: [],
        idProperty: 'idConfigPedidoStatus',
    });

    const [filterDsConfiguracao, setFilterDsConfiguracao] = useState<FilterValuesProps>({
        property: 'dsConfigPedido',
        operator: 'like',
        value: '',
        logicalOperator: 'OR',
    });

    const [filterIdConfiguracao, setFilterIdConfiguracao] = useState<FilterValuesProps>({
        property: 'idConfigPedido',
        operator: 'eq',
        value: '',
        logicalOperator: 'OR',
    });

    const [filterStatus, setFilterStatus] = useState<FilterValuesProps>({
        property: 'fgAtivo',
        operator: 'eq',
        value: null,
        idProperty: 'vlStatus',
    });

    const [filterInicioVigencia, setFilterInicioVigencia] = useState<FilterValuesProps>({
        property: 'dtInicioVigencia',
        operator: 'gte',
        value: getInicioVigencia(),
        required: 'Início de Vigência é obrigatório',
    });

    const [filterFimVigencia, setFilterFimVigencia] = useState<FilterValuesProps>({
        property: 'dtFimVigencia',
        operator: 'lte',
        value: getFimVigencia(),
        required: 'Fim de Vigência é obrigatório',
    });

    const [filterTipoPedido, setFilterTipoPedido] = useState<FilterValuesProps>({
        property: 'idTipoPedido',
        operator: 'in',
        value: [],
        idProperty: 'idTipoPedido',
    });

    return (
        <Filter
            preventQueryParamsEmptyStart
            filters={[
                { value: filterDsConfiguracao, setter: setFilterDsConfiguracao },
                { value: filterIdConfiguracao, setter: setFilterIdConfiguracao },
                { value: filterProduto, setter: setFilterProduto },
                { value: filterGrade, setter: setFilterGrade },
                { value: filterTipoPedido, setter: setFilterTipoPedido },
                { value: filterSitucao, setter: setFilterSitucao },
                { value: filterStatus, setter: setFilterStatus },
                { value: filterInicioVigencia, setter: setFilterInicioVigencia },
                { value: filterFimVigencia, setter: setFilterFimVigencia },
            ]}
        >
            <Grid size={{ lg: 3 }}>
                <CissTextField
                    label="Configuração"
                    placeholder="Nome ou código da configuração..."
                    value={filterDsConfiguracao.value}
                    onChange={(e) => {
                        setFilterDsConfiguracao((oldState: FilterValuesProps) => ({ ...oldState, value: e.target.value }));
                        setFilterIdConfiguracao((oldState: FilterValuesProps) => ({ ...oldState, value: e.target.value }));
                    }}
                />
            </Grid>

            <Grid size={{ lg: 3 }}>
                <ProdutoAutoComplete
                    label="Produtos"
                    value={filterProduto.value}
                    onChange={(e, value: ProdutoProps[]) =>
                        setFilterProduto((oldState: FilterValuesProps) => ({
                            ...oldState,
                            value: value.map((item) => ({ idProduto: item.idProduto, dsProduto: item.dsProduto, cdProduto: item.cdProduto })),
                        }))
                    }
                />
            </Grid>

            <Grid size={{ lg: 3 }}>
                <GradeAutoComplete
                    label="Grade"
                    multiple={true}
                    value={filterGrade.value}
                    onChange={(e, value: GradeOptionsProps[]) =>
                        setFilterGrade((oldState: FilterValuesProps) => ({
                            ...oldState,
                            value: value.map((item) => ({ idTipoGrade: item.idTipoGrade, dsTipoGrade: item.dsTipoGrade })),
                        }))
                    }
                />
            </Grid>

            <Grid size={{ lg: 3 }}>
                <TipoPedidoAutoComplete
                    multiple={true}
                    value={filterTipoPedido.value}
                    onChange={(e, value) => setFilterTipoPedido((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>

            <Grid size={{ lg: 3 }}>
                <SituacaoConfiguracaoPedidoAutoComplete
                    multiple={true}
                    value={filterSitucao.value}
                    onChange={(e, value) => setFilterSitucao((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>

            <Grid size={{ lg: 3 }}>
                <StatusField
                    value={filterStatus.value}
                    onChange={(e, value) => setFilterStatus((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>

            <Grid size={{ lg: 3 }}>
                <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                    <DatePicker
                        sx={{ width: '100%' }}
                        label="Início de Vigência"
                        format="dd/MM/yyyy"
                        value={filterInicioVigencia.value}
                        onChange={(value) => setFilterInicioVigencia((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size={{ lg: 3 }}>
                <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                    <DatePicker
                        sx={{ width: '100%' }}
                        label="Fim de Vigência"
                        format="dd/MM/yyyy"
                        value={filterFimVigencia.value}
                        onChange={(value) => setFilterFimVigencia((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                    />
                </LocalizationProvider>
            </Grid>
        </Filter>
    );
}
